/* Guild saved items and comparison. Shopify product pages remain authoritative.
 * Store product/variant identity only: never persist a price or stock assertion.
 */
(() => {
  'use strict';
  if (window.GuildCommerce) return;
  const withArrow = node => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    const path = document.createElementNS(svg.namespaceURI, 'path');
    for (const [key, value] of Object.entries({class: 'cg-icon', viewBox: '0 0 24 24', width: '1em', height: '1em', 'aria-hidden': 'true', focusable: 'false', fill: 'none', stroke: 'currentColor', 'stroke-width': '1.5', 'stroke-linecap': 'square'})) svg.setAttribute(key, value);
    path.setAttribute('d', 'M5 19 19 5M5 5h14v14');
    svg.append(path); node.append(' ', svg);
    return node;
  };
  const storageKey = 'collectors-guild:products:v1';
  const root = window.Shopify?.routes?.root || '/';
  const records = new Map();
  const selected = new Map();
  const requests = new Map();
  const observedRecommendations = new WeakSet();
  let state = { saved: [], compared: [] };
  let persistent = true;
  let renderVersion = 0;
  let dialog;
  let savedDialog;
  let compareBar;
  let comparisonResponse = 0;
  let noticeTimer;
  let noticeAnchor;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const narrowScreen = window.matchMedia('(max-width: 749px)');
  const activeResponses = new Map();
  const responseObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => { if (!isIntersecting) stopResponse(target); });
  }) : null;
  const pixelsOn = () => document.documentElement.dataset.cgPixels !== 'off';
  const motionOff = () => reducedMotion.matches || document.hidden || document.body.classList.contains('vc-motion-paused');

  function stopResponse(node) {
    const finish = activeResponses.get(node);
    if (!finish) return;
    node.classList.remove('is-pixel-responding', 'cg-save-pop');
    node.removeEventListener('animationend', finish);
    node.removeEventListener('animationcancel', finish);
    activeResponses.delete(node);
    responseObserver?.unobserve(node);
  }
  function stopResponses() { [...activeResponses.keys()].forEach(stopResponse); }
  function respond(node, className = 'is-pixel-responding') {
    stopResponse(node);
    if (motionOff() || (className === 'is-pixel-responding' && !pixelsOn())) return;
    const bounds = node.getBoundingClientRect();
    if (!node.isConnected || !bounds.width || !bounds.height || bounds.bottom <= 0 || bounds.top >= innerHeight || bounds.right <= 0 || bounds.left >= innerWidth) return;
    const finish = () => stopResponse(node);
    activeResponses.set(node, finish);
    node.addEventListener('animationend', finish);
    node.addEventListener('animationcancel', finish);
    responseObserver?.observe(node);
    node.classList.add(className);
  }
  function pixelArt(kind) {
    const wrapper = element('span', `cg-pixel-detail cg-pixel-${kind}`);
    wrapper.setAttribute('aria-hidden', 'true');
    // Original grid drawings: paper sleeve / binder and a generic connecting cable.
    // No game sprites, character artwork or external asset request.
    wrapper.innerHTML = kind === 'storage'
      ? '<svg viewBox="0 0 32 28" width="64" height="56" fill="none" focusable="false"><path class="cg-pixel-ink" d="M6 8h20v2h2v16H4V10h2z"/><path class="cg-pixel-paper" d="M6 10h20v14H6z"/><path class="cg-pixel-support" d="M8 12h16v10H8z"/><g class="cg-pixel-sleeve"><path class="cg-pixel-ink" d="M10 0h12v17H10z"/><path class="cg-pixel-paper" d="M12 2h8v13h-8z"/><path class="cg-pixel-accent" d="M14 4h4v2h2v4h-2v2h-4v-2h-2V6h2z"/></g><path class="cg-pixel-ink" d="M4 16h24v10H4z"/><path class="cg-pixel-field" d="M6 18h20v6H6z"/><path class="cg-pixel-ink" d="M14 20h2v2h-2zm2 2h2v2h-2zm2-2h2v2h-2zm2-2h2v2h-2z"/></svg>'
      : '<svg viewBox="0 0 64 16" width="128" height="32" fill="none" focusable="false"><g class="cg-pixel-cable-left"><path class="cg-pixel-ink" d="M0 6h18V2h12v2h4v8h-4v2H18v-4H0z"/><path class="cg-pixel-field" d="M20 4h8v8h-8z"/><path class="cg-pixel-paper" d="M30 6h2v4h-2z"/></g><g class="cg-pixel-cable-right"><path class="cg-pixel-ink" d="M64 6H46V2H34v12h12v-4h18z"/><path class="cg-pixel-support" d="M36 4h8v8h-8z"/><path class="cg-pixel-accent" d="M38 6h4v4h-4z"/></g></svg>';
    return wrapper;
  }
  function pokemonOnly(refs) {
    return refs.length > 0 && refs.every((ref) => records.get(ref.id)?.product?.franchise === 'pokemon');
  }
  function updateBinderDetail(grid) {
    let note = grid.previousElementSibling;
    if (!note?.hasAttribute('data-guild-binder-lore')) {
      note = element('p', 'cg-pixel-detail cg-pixel-binder-note');
      note.dataset.guildBinderLore = '';
      note.hidden = true;
      grid.before(note);
    }
    note.textContent = pokemonOnly(state.saved) ? 'A nod to Bill’s PC storage from the Pokémon games. Your binder keeps a shortlist, not stock.' : 'Your browser keeps this shortlist. Open each listing to check its current product details.';
    note.hidden = state.saved.length === 0;
  }

  const validId = (value) => /^\d+$/.test(String(value || ''));
  const validHandle = (value) => typeof value === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(value);
  const references = (items, limit) => {
    const seen = new Set();
    return (Array.isArray(items) ? items : []).filter((item) => {
      if (!validId(item?.id) || !validHandle(item?.handle) || seen.has(String(item.id))) return false;
      seen.add(String(item.id));
      return true;
    }).slice(0, limit).map((item) => ({ id: String(item.id), handle: item.handle, variantId: validId(item.variantId) ? String(item.variantId) : '' }));
  };
  function readStorage() {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '{}');
      state = { saved: references(saved.saved, 40), compared: references(saved.compared, 3) };
    } catch { persistent = false; }
  }
  function persist() {
    try { localStorage.setItem(storageKey, JSON.stringify(state)); persistent = true; }
    catch { persistent = false; }
  }
  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
  function inlineNotice(status) {
    clearTimeout(noticeTimer);
    const anchor = noticeAnchor?.isConnected ? noticeAnchor : document.querySelector('main') || document.body;
    status.classList.add('cg-commerce-notice-inline');
    status.style.removeProperty('left');
    status.style.removeProperty('top');
    status.style.removeProperty('bottom');
    status.style.removeProperty('transform');
    if (anchor === document.body) anchor.append(status);
    else anchor.after(status);
  }
  function placeNotice(status, trigger) {
    // Never grow one card: its grid peers may have bottom-aligned controls.
    // A constrained viewport keeps the message after the whole grid instead.
    noticeAnchor = trigger?.closest('.cg-product-grid, .cg-saved-grid, .product-grid, .cg-compare-grid') ||
      trigger?.closest('.cg-pdp-actions, .cg-product-actions, .cg-section-heading') || trigger;
    document.body.append(status);
    status.classList.remove('cg-commerce-notice-inline', 'cg-commerce-notice-suppressed');
    status.style.left = '16px';
    status.style.top = '16px';
    status.style.bottom = 'auto';
    status.style.transform = 'none';
    const bounds = status.getBoundingClientRect();
    // Four fixed corners, with real content rectangles and an 8px clear margin.
    // If none is clear, normal document flow is the safer response.
    const protectedNodes = document.querySelectorAll('button, a, input, select, textarea, summary, label, header, [role="dialog"], #cg-design-review, #local-preview-notice, .product__media-wrapper, .product__info-container, .cg-product-card, main h1, main h2, main h3, main p, main dl');
    const obstacles = [...protectedNodes].filter(node => node !== status && !status.contains(node) && node.getClientRects().length).map(node => node.getBoundingClientRect()).filter(rect => rect.width && rect.height && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth);
    const clear = ({ left, top }) => obstacles.every(rect => left + bounds.width + 8 < rect.left || left - 8 > rect.right || top + bounds.height + 8 < rect.top || top - 8 > rect.bottom);
    const candidates = [
      { left: 16, top: 16 }, { left: innerWidth - bounds.width - 16, top: 16 },
      { left: 16, top: innerHeight - bounds.height - 16 }, { left: innerWidth - bounds.width - 16, top: innerHeight - bounds.height - 16 }
    ];
    const available = candidates.find(point => point.left >= 16 && point.top >= 16 && clear(point));
    if (available && !trigger?.closest('dialog')) {
      status.style.left = `${available.left}px`;
      status.style.top = `${available.top}px`;
    } else inlineNotice(status);
  }
  function announce(message, savedResponse = false, trigger = null) {
    let status = document.querySelector('[data-guild-announcement]');
    if (!status) {
      status = element('p', 'cg-commerce-notice');
      status.dataset.guildAnnouncement = '';
      status.setAttribute('role', 'status');
      document.body.append(status);
    }
    status.querySelectorAll('.cg-pixel-detail').forEach(stopResponse);
    status.classList.toggle('cg-pixel-save-notice', savedResponse && persistent && pixelsOn());
    status.replaceChildren();
    const art = savedResponse && persistent && pixelsOn() ? pixelArt('storage') : null;
    if (art) status.append(art);
    status.append(element('span', '', message + (persistent ? '' : ' Your browser could not save this list; it is available only on this page.')));
    placeNotice(status, trigger);
    if (art) respond(art);
    clearTimeout(noticeTimer);
    // Inline feedback stays populated. A delayed collapse would shift the page.
    if (!status.classList.contains('cg-commerce-notice-inline')) noticeTimer = setTimeout(() => {
      status.querySelectorAll('.cg-pixel-detail').forEach(stopResponse);
      status.replaceChildren(); status.classList.remove('cg-pixel-save-notice');
    }, 7000);
  }
  function productURL(ref) {
    const url = new URL(`${root}products/${encodeURIComponent(ref.handle)}`, location.origin);
    if (validId(ref.variantId)) url.searchParams.set('variant', ref.variantId);
    return url;
  }
  function validProduct(value) {
    return value && validId(value.id) && validHandle(value.handle) && typeof value.title === 'string' &&
      Array.isArray(value.variants) && value.variants.every((variant) => validId(variant.id) && typeof variant.price === 'string');
  }
  function readProducts(scope = document) {
    scope.querySelectorAll('script[data-guild-product]').forEach((script) => {
      try {
        const data = JSON.parse(script.textContent);
        if (validProduct(data)) {
          data.id = String(data.id);
          records.set(data.id, { product: data });
          if (validId(data.variantId)) selected.set(data.id, String(data.variantId));
        }
      } catch { /* Native product links remain usable when optional data is absent. */ }
    });
  }
  function observeRecommendations(scope = document) {
    const regions = [...scope.querySelectorAll('product-recommendations')];
    if (scope.matches?.('product-recommendations')) regions.push(scope);
    regions.forEach((region) => {
      if (observedRecommendations.has(region)) return;
      observedRecommendations.add(region);
      // Dawn fetches and replaces this region when it approaches the viewport.
      // React only to new catalogue scripts, not our own button text updates.
      new MutationObserver((mutations) => {
        const receivedProducts = mutations.some((mutation) => [...mutation.addedNodes].some((node) =>
          node.nodeType === Node.ELEMENT_NODE && (node.matches('script[data-guild-product]') || node.querySelector('script[data-guild-product]'))
        ));
        if (!receivedProducts) return;
        readProducts(region);
        updateButtons();
      }).observe(region, { childList: true, subtree: true });
    });
  }
  function referenceFor(id) {
    const product = records.get(id)?.product;
    if (!product) return null;
    const existing = state.saved.find((item) => item.id === id) || state.compared.find((item) => item.id === id);
    const variantId = selected.get(id) || existing?.variantId || (product.variants.length === 1 ? String(product.variants[0].id) : '');
    return { id, handle: product.handle, variantId };
  }
  async function refreshProduct(ref) {
    if (requests.has(ref.id)) return requests.get(ref.id);
    const request = (async () => {
      try {
        const response = await fetch(productURL(ref), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' }, signal: AbortSignal.timeout(12000) });
        if (response.status === 404 || response.status === 410) return { error: 'removed' };
        if (!response.ok) return { error: 'network' };
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        for (const script of html.querySelectorAll('script[data-guild-product]')) {
          try {
            const product = JSON.parse(script.textContent);
            if (validProduct(product) && String(product.id) === ref.id) return { product };
          } catch { /* A malformed script cannot become a product or price. */ }
        }
        return { error: 'unverified' };
      } catch { return { error: 'network' }; }
    })();
    requests.set(ref.id, request);
    const result = await request;
    records.set(ref.id, result);
    requests.delete(ref.id);
    return result;
  }
  function updateButtons() {
    ['save', 'compare'].forEach((action) => {
      const list = action === 'save' ? state.saved : state.compared;
      document.querySelectorAll(`[data-guild-${action}]`).forEach((button) => {
        const id = button.dataset[action === 'save' ? 'guildSave' : 'guildCompare'];
        const product = records.get(id)?.product;
        if (!product) return;
        const active = list.some((ref) => ref.id === id);
        button.hidden = false;
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-label', `${active ? 'Remove' : action === 'save' ? 'Save' : 'Compare'} ${product.title}${active ? action === 'save' ? ' from saved items' : ' from comparison' : ''}`);
        button.textContent = action === 'save' ? active ? 'Saved' : 'Save' : active ? 'Comparing' : 'Compare';
      });
    });
    document.querySelectorAll('[data-guild-saved-count]').forEach((node) => { node.textContent = state.saved.length; });
    document.querySelectorAll('[data-guild-open-saved]').forEach((node) => { node.hidden = false; });
    if (!compareBar) {
      compareBar = element('aside', 'cg-compare-bar');
      compareBar.setAttribute('aria-label', 'Product comparison');
      const open = element('button', 'cg-compare-launcher', 'Compare selections');
      open.type = 'button';
      open.dataset.guildOpenCompare = '';
      compareBar.append(element('span', 'cg-compare-bar-label', 'Your comparison'), open);
      document.body.append(compareBar);
      if ('ResizeObserver' in window) new ResizeObserver(reserveComparisonSpace).observe(compareBar);
    }
    compareBar.hidden = state.compared.length === 0;
    compareBar.querySelector('[data-guild-open-compare]').textContent = `Compare selections (${state.compared.length})`;
    reserveComparisonSpace();
  }
  function reserveComparisonSpace() {
    const active = state.compared.length > 0;
    document.body.classList.toggle('cg-has-comparison', active);
    document.documentElement.classList.toggle('cg-has-comparison', active);
    document.documentElement.style.setProperty('--cg-compare-space', active ? `${Math.ceil(compareBar.getBoundingClientRect().height) + 12}px` : '0px');
    if (active) requestAnimationFrame(() => keepComparisonFocusClear(document.activeElement));
  }
  function keepComparisonFocusClear(target) {
    if (!state.compared.length || !compareBar || compareBar.hidden || !(target instanceof HTMLElement) || target === document.body ||
        compareBar.contains(target) || target.closest('dialog, [role="dialog"], .drawer.active') || getComputedStyle(compareBar).visibility === 'hidden') return;
    const bounds = target.getBoundingClientRect();
    const rail = compareBar.getBoundingClientRect();
    if (bounds.width && bounds.height && bounds.bottom > rail.top && bounds.top < rail.bottom && bounds.right > rail.left && bounds.left < rail.right) {
      // End padding makes this scroll possible even for the last footer control.
      // Run after native focus scrolling; a fixed rail is not part of its layout.
      window.scrollBy({ top: bounds.bottom - rail.top + 12, behavior: 'instant' });
    }
  }
  function actionButton(action, ref, label) {
    const button = element('button', `cg-${action}`, label);
    button.type = 'button';
    button.dataset[action === 'save' ? 'guildSave' : 'guildCompare'] = ref.id;
    return button;
  }
  function removeButton(ref, list) {
    const button = element('button', 'cg-text-link', 'Remove');
    button.type = 'button';
    button.dataset.guildRemove = ref.id;
    button.dataset.guildList = list;
    button.setAttribute('aria-label', `Remove ${records.get(ref.id)?.product?.title || ref.handle.replaceAll('-', ' ')} from ${list === 'compared' ? 'comparison' : 'saved items'}`);
    return button;
  }
  function photo(product, variant, variantGone) {
    const frame = element('div', 'cg-product-media');
    const source = variantGone ? null : variant ? variant.image : product.image;
    const pending = variantGone ? 'Select another variant to see its photograph.' : variant ? 'No photograph is listed for this variant.' : 'Product photography pending';
    if (!source?.src) {
      frame.append(element('span', 'cg-image-pending', pending));
      return frame;
    }
    const image = element('img', 'cg-product-image');
    try {
      const src = new URL(source.src, location.origin);
      if (!['http:', 'https:'].includes(src.protocol)) throw new Error('Invalid image');
      image.src = src.href;
      if (source.small) {
        const small = new URL(source.small, location.origin);
        if (['http:', 'https:'].includes(small.protocol)) image.srcset = `${small.href} 360w, ${src.href} 720w`;
      }
      image.sizes = '(min-width: 750px) 30vw, 90vw';
      image.alt = source.alt || product.title;
      image.width = source.width || 720;
      image.height = source.height || 720;
      image.loading = 'lazy';
      image.addEventListener('error', () => frame.replaceChildren(element('span', 'cg-image-pending', 'Product image could not be loaded.')), { once: true });
      frame.append(image);
    } catch { frame.append(element('span', 'cg-image-pending', pending)); }
    return frame;
  }
  function detailValue(value) {
    const scalar = item => typeof item === 'string' || typeof item === 'number' ? String(item).trim() : '';
    // Shopify text lists and rich text are structured values, not missing facts.
    // Extract only supported text nodes; all output is inserted as textContent.
    const richText = (node, depth = 0) => {
      if (!node || typeof node !== 'object' || depth > 20) return '';
      if (node.type === 'text') return typeof node.value === 'string' ? node.value : '';
      if (!['root', 'paragraph', 'heading', 'list', 'list-item', 'link'].includes(node.type) || !Array.isArray(node.children)) return '';
      const separator = ['root', 'list'].includes(node.type) ? '\n' : '';
      return node.children.map(child => richText(child, depth + 1)).join(separator);
    };
    const text = Array.isArray(value) ? value.map(scalar).filter(Boolean).join(', ') : value?.type === 'root' ? richText(value) : scalar(value);
    return text.trim() || 'Not specified';
  }
  function productCard(ref, mode, presentation) {
    const comparison = mode !== 'saved';
    const article = element('article', comparison ? 'cg-compare-product' : 'cg-product-card');
    const paper = !comparison && presentation === 'paper';
    if (paper) article.classList.add('cg-paper-product');
    else if (!comparison) article.classList.add('cg-saved-paper');
    const record = records.get(ref.id);
    if (!record?.product) {
      const message = !record ? 'Checking current product details…' : record.error === 'removed' ? 'This product is no longer listed.' : 'Current product details could not be verified.';
      const media = element('div', 'cg-product-media');
      media.append(element('span', 'cg-image-pending', !record ? 'Checking product…' : 'Product image unavailable'));
      const info = element('div', 'cg-product-info');
      info.append(element('h3', 'cg-product-title', ref.handle.replaceAll('-', ' ')), element('p', 'cg-product-availability', message));
      if (record?.error !== 'removed') {
        const link = element('a', 'cg-text-link', 'Open product');
        link.href = productURL(ref);
        info.append(link);
        const retry = element('button', 'cg-text-link', 'Try again');
        retry.type = 'button';
        retry.dataset.guildRetry = ref.id;
        info.append(retry);
      }
      info.append(removeButton(ref, comparison ? 'compared' : 'saved'));
      article.append(media, info);
      return article;
    }
    const product = record.product;
    if (typeof product.details?.format === 'string') article.dataset.productFormat = product.details.format.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const variant = product.variants.find((item) => String(item.id) === ref.variantId);
    const variantGone = Boolean(ref.variantId && !variant);
    const url = productURL({ ...ref, variantId: variant ? ref.variantId : '' });
    const mediaLink = element('a', 'cg-product-image-link');
    mediaLink.href = url;
    mediaLink.setAttribute('aria-label', `View ${product.title}`);
    mediaLink.append(photo(product, variant, variantGone));
    const info = element('div', 'cg-product-info');
    const heading = element('h3', 'cg-product-title');
    const link = element('a', '');
    const productSet = typeof product.details?.set === 'string' ? product.details.set.trim() : '';
    const productFormat = typeof product.details?.format === 'string' ? product.details.format.trim() : '';
    link.append(element('span', 'cg-title-set', productSet || product.title));
    if (productSet && productFormat) link.append(document.createTextNode(' '), element('span', 'cg-title-format', productFormat));
    link.href = url;
    heading.append(link);
    const price = variantGone ? 'Select another variant to see its price.' : variant ? variant.price : `${product.priceVaries ? 'From ' : ''}${product.price}`;
    const availability = variantGone ? 'Your selected variant is no longer listed.' : product.availabilityUnknown ? 'Availability to be confirmed' : variant ? variant.available ? 'In stock' : 'Selected variant sold out' : product.available ? 'In stock; choose a variant where offered.' : 'Sold out';
    const summary = (comparison ? [] : [product.details?.language]).filter(Boolean).map(detailValue);
    if (!comparison && product.details?.boosterPacks) summary.push(`${product.details.boosterPacks} booster packs`);
    info.append(heading);
    if (summary.length) info.append(element('p', 'cg-product-meta', summary.join(' · ')));
    info.append(element('p', 'cg-product-price', price));
    if (!variantGone && (product.approvedPrice || product.proposedPrice)) {
      info.append(element('p', 'cg-proposed-price', product.approvedPrice ? 'Approved retail · private review' : 'Proposed retail · private review'));
    }
    info.append(element('p', 'cg-product-availability', availability));
    if (product.variants.length > 1 || variantGone) {
      const label = element('label', 'cg-variant-label', 'Choose a variant');
      const select = element('select', 'cg-variant-select');
      select.id = `GuildVariant-${mode}-${ref.id}`;
      label.htmlFor = select.id;
      select.dataset.guildVariant = ref.id;
      const placeholder = element('option', '', 'Choose a variant');
      placeholder.value = '';
      select.append(placeholder);
      product.variants.forEach((item) => {
        const option = element('option', '', `${item.title}${product.availabilityUnknown ? '' : item.available ? '' : ' · Sold out'}`);
        option.value = item.id;
        select.append(option);
      });
      select.value = variant ? ref.variantId : '';
      info.append(label, select);
    }
    const inspect = withArrow(element('a', 'cg-text-link cg-product-view', 'View product'));
    inspect.href = url;
    if (comparison) {
      const actions = element('div', 'cg-comparison-actions');
      inspect.textContent = 'View';
      withArrow(inspect);
      inspect.setAttribute('aria-label', `View ${product.title}`);
      actions.append(inspect, removeButton(ref, 'compared'));
      info.append(actions);
    } else {
      const actions = element('div', 'cg-product-actions');
      actions.append(removeButton(ref, 'saved'), actionButton('compare', ref, 'Compare'));
      info.append(inspect, actions);
    }
    if (paper) {
      const set = detailValue(product.details?.set);
      article.dataset.cardScene = ({'Destined Rivals':'rivals','Ascended Heroes':'heroes','Prismatic Evolutions':'prismatic'})[set] || '';
      const pin = element('span', 'cg-card-pin'); pin.setAttribute('aria-hidden','true');
      article.append(pin);
      const frame = mediaLink.querySelector('.cg-product-media');
      const image = frame.querySelector('.cg-product-image');
      if (image) {
        // Explicit, visually verified Shopify alias; never infer a shape from arbitrary replacement names.
        const known = [['cg-ascended-heroes-etb-cherry-front','cherry-heroes-etb'],['cg-ascended-heroes-etb-cherry-front_8f095cf6-af51-4e27-af04-18cb9bac9c56','cherry-heroes-etb'],['cg-ascended-heroes-booster-bundle-cherry-front','cherry-heroes-bundle'],['cg-prismatic-evolutions-spc-cherry-front','cherry-prismatic-spc'],['cg-destined-rivals-etb-front','original-rivals-etb'],['cg-prismatic-evolutions-etb-front','original-prismatic-etb']];
        article.dataset.packshotShape = known.find(([filename])=>new URL(image.src,location.href).pathname.split('/').pop() === `${filename}.jpg`)?.[1] || '';
        const packshot = element('span','cg-card-packshot'); image.replaceWith(packshot); packshot.append(image);
      }
      const spark = element('span','cg-card-spark','✦'); spark.setAttribute('aria-hidden','true'); frame.append(spark);
      // Scenery is a CSS decoration shared with the server presentation; no product data changes.
      frame.classList.add('cg-card-dynamic-scenery');
    }
    article.append(mediaLink, info);
    return article;
  }
  function renderComparison(grid) {
    const previousScroll = grid.querySelector('.cg-compare-scroll')?.scrollLeft || 0;
    if (!state.compared.length) {
      grid.replaceChildren(element('p', 'cg-empty', 'Choose Compare on a product to look at it here.'));
      return;
    }
    const mode = grid.closest('dialog') ? 'dialog' : 'compare';
    const table = element('table', 'cg-compare-table');
    table.style.setProperty('--cg-compare-count', state.compared.length);
    table.append(element('caption', 'visually-hidden', 'Selected products compared by the same attributes'));
    const head = element('thead');
    const headings = element('tr');
    const label = element('th', 'cg-compare-axis', 'Details');
    label.scope = 'col';
    headings.append(label);
    state.compared.forEach((ref) => {
      const cell = element('th', 'cg-compare-heading');
      cell.scope = 'col';
      cell.id = `GuildCompare-${mode}-${ref.id}`;
      cell.append(productCard(ref, mode));
      headings.append(cell);
    });
    head.append(headings);
    const body = element('tbody');
    const shared = element('section', 'cg-compare-shared');
    const sharedHeading = element('h3', '', 'Shared details');
    sharedHeading.id = `GuildCompare-${mode}-shared`;
    shared.setAttribute('aria-labelledby', sharedHeading.id);
    const sharedFacts = element('dl');
    shared.append(sharedHeading, element('p', '', 'Applies to every selection.'), sharedFacts);
    [['format', 'Format'], ['boosterPacks', 'Booster packs'], ['set', 'Set'], ['language', 'Language'], ['edition', 'Edition'], ['contents', 'Contents']].forEach(([key, title]) => {
      const rawValues = state.compared.map(ref => records.get(ref.id)?.product?.details?.[key]);
      // Only explicit, identical plain-text metadata can describe every selection.
      // Missing/failed records and display fallbacks must remain product-specific.
      const isShared = state.compared.length > 1 && ['set', 'language', 'edition'].includes(key) &&
        rawValues.every(value => typeof value === 'string' && value.trim() &&
          !/^(not specified|details unavailable|unknown|unconfirmed|not confirmed|to be confirmed)$/i.test(value.trim()) && value === rawValues[0]);
      if (isShared) {
        const fact = element('div');
        fact.dataset.compareAttribute = key;
        fact.append(element('dt', '', title), element('dd', 'cg-compare-value', rawValues[0]));
        sharedFacts.append(fact);
        return;
      }
      const row = element('tr', 'cg-compare-fact');
      row.dataset.compareAttribute = key;
      const titleCell = element('th', 'cg-compare-axis', title);
      titleCell.scope = 'row';
      titleCell.id = `GuildCompare-${mode}-${key}`;
      row.append(titleCell);
      const values = state.compared.map(ref => records.get(ref.id)?.product ? detailValue(records.get(ref.id).product.details?.[key]) : 'Details unavailable');
      row.classList.toggle('cg-compare-difference', new Set(values).size > 1);
      state.compared.forEach((ref, index) => {
        const cell = element('td');
        cell.setAttribute('headers', `GuildCompare-${mode}-${ref.id} ${titleCell.id}`);
        // Keep the product in view on long mobile rows, even after its header scrolls away.
        const context = element('span', 'cg-compare-context', records.get(ref.id)?.product?.title || ref.handle.replaceAll('-', ' '));
        context.setAttribute('aria-hidden', 'true');
        const rawValue = records.get(ref.id)?.product?.details?.[key];
        const contents = key === 'contents' && Array.isArray(rawValue) ? rawValue.filter(item => typeof item === 'string' || typeof item === 'number').map(String).filter(item => item.trim()) : [];
        cell.append(context);
        if (contents.length) {
          const list = element('ul', 'cg-compare-value cg-compare-contents');
          contents.forEach(item => list.append(element('li', '', item)));
          cell.append(list);
        } else cell.append(element('span', 'cg-compare-value', values[index]));
        row.append(cell);
      });
      body.append(row);
    });
    table.append(head, body);
    const scroll = element('div', 'cg-compare-scroll');
    scroll.tabIndex = 0;
    scroll.setAttribute('role', 'region');
    scroll.setAttribute('aria-label', 'Product comparison. Scroll sideways to see every selection.');
    scroll.append(table);
    grid.replaceChildren(element('p', 'cg-compare-help', 'Scroll across; tinted rows differ.'), scroll);
    if (sharedFacts.childElementCount) grid.append(shared);
    scroll.scrollLeft = previousScroll;
  }
  function render() {
    const focused = document.activeElement;
    const focusAttribute = ['data-guild-variant', 'data-guild-remove', 'data-guild-save', 'data-guild-compare'].find((attribute) => focused?.hasAttribute(attribute));
    const focusValue = focusAttribute ? focused.getAttribute(focusAttribute) : '';
    const focusDialog = focused?.closest('dialog');
    const focusedScroller = focused?.classList.contains('cg-compare-scroll');
    document.querySelectorAll('[data-guild-saved-grid]').forEach((grid) => {
      grid.replaceChildren(...state.saved.map((ref) => productCard(ref, 'saved', grid.dataset.guildCardPresentation)));
      updateBinderDetail(grid);
    });
    document.querySelectorAll('[data-guild-saved-empty]').forEach((empty) => { empty.hidden = state.saved.length > 0; });
    if (!state.saved.length) document.querySelectorAll('[data-guild-saved-status]').forEach(status => { status.textContent = ''; });
    document.querySelectorAll('[data-guild-comparison]').forEach((region) => { region.hidden = state.compared.length === 0; });
    document.querySelectorAll('[data-guild-comparison-grid]').forEach(renderComparison);
    updateButtons();
    if (focusedScroller && !focused.isConnected) (focusDialog || document).querySelector('.cg-compare-scroll')?.focus({ preventScroll: true });
    if (focusAttribute && !focused.isConnected) {
      const replacement = [...document.querySelectorAll(`[${focusAttribute}]`)].find((node) => node.getAttribute(focusAttribute) === focusValue && node.closest('dialog') === focusDialog);
      replacement?.focus({ preventScroll: true });
    }
  }
  async function refreshLists() {
    const version = ++renderVersion;
    document.querySelectorAll('[data-guild-saved-status]').forEach((status) => { status.textContent = state.saved.length ? 'Checking current prices and availability…' : ''; });
    const refs = [...new Map([...state.saved, ...state.compared].map((ref) => [ref.id, ref])).values()];
    await Promise.all(refs.map(refreshProduct));
    if (version !== renderVersion) return;
    render();
    const failed = refs.some((ref) => records.get(ref.id)?.error);
    document.querySelectorAll('[data-guild-saved-status]').forEach((status) => { status.textContent = failed ? 'Some items could not be refreshed. Their current prices and availability are not shown.' : state.saved.length ? 'Showing current catalogue details. Saving an item does not reserve stock.' : ''; });
  }
  function openComparison() {
    if (!dialog) {
      dialog = element('dialog', 'cg-comparison-dialog');
      dialog.setAttribute('aria-labelledby', 'GuildCompareDialogTitle');
      const header = element('header', 'cg-section-heading');
      const heading = element('h2', '', 'Compare your selections');
      heading.id = 'GuildCompareDialogTitle';
      const close = element('button', 'cg-text-link cg-comparison-close', 'Close');
      close.setAttribute('aria-label', 'Close comparison');
      close.type = 'button';
      close.addEventListener('click', () => dialog.close());
      const grid = element('div', 'cg-compare-grid');
      grid.dataset.guildComparisonGrid = '';
      const title = element('div', 'cg-pixel-comparison-title');
      title.append(heading, pixelArt('cable'));
      header.append(title, close);
      dialog.append(header, grid,
        element('p', 'cg-compare-help', 'Current listing details, with your selected variant. “Not specified” means the listing has not supplied that detail. Selections stay in this browser. Saving or comparing does not reserve stock.'));
      document.body.append(dialog);
      dialog.addEventListener('close', () => {
        comparisonResponse++;
        dialog.querySelectorAll('.cg-pixel-detail').forEach(stopResponse);
        if (!state.compared.length) {
          const scope = savedDialog?.open ? savedDialog : document;
          scope.querySelector('[data-guild-compare]:not([hidden]), [data-guild-open-compare]:not([hidden])')?.focus({ preventScroll: true });
        }
      });
    }
    render();
    dialog.showModal();
    const cable = dialog.querySelector('.cg-pixel-cable');
    cable.hidden = state.compared.length === 0;
    const response = ++comparisonResponse;
    if (state.compared.length > 0 && !narrowScreen.matches) {
      // The dialog's curtain initially clips the cable. Start its response once
      // that entrance is visible, otherwise the offscreen guard cancels it.
      requestAnimationFrame(() => {
        const entrance = dialog.getAnimations().map(animation => animation.finished.catch(() => {}));
        Promise.all(entrance).then(() => {
          if (response === comparisonResponse && dialog.open && state.compared.length > 0 && !narrowScreen.matches) respond(cable);
        });
      });
    }
    refreshLists();
  }
  function openSaved() {
    if (!savedDialog) {
      savedDialog = element('dialog', 'cg-saved-dialog');
      savedDialog.setAttribute('aria-labelledby', 'GuildSavedDialogTitle');
      const header = element('header', 'cg-section-heading');
      const heading = element('h2', '', 'Your saved binder');
      heading.id = 'GuildSavedDialogTitle';
      const close = element('button', 'cg-text-link', 'Close saved items');
      close.type = 'button';
      close.addEventListener('click', () => savedDialog.close());
      header.append(heading, close);
      const content = element('section', 'cg-saved');
      content.dataset.guildSaved = '';
      const status = element('p', 'cg-commerce-status');
      status.dataset.guildSavedStatus = '';
      status.setAttribute('role', 'status');
      const grid = element('div', 'cg-saved-grid cg-product-grid');
      grid.dataset.guildSavedGrid = '';
      const empty = element('div', 'cg-empty', 'You haven’t saved anything yet. Select Save on a product to keep it here. Clearing browser storage removes your list.');
      empty.dataset.guildSavedEmpty = '';
      const browse = withArrow(element('a', 'cg-text-link', 'Explore the catalogue'));
      browse.href = `${root}collections/all`;
      const compare = element('button', 'cg-text-link', 'Compare selections');
      compare.type = 'button';
      compare.dataset.guildOpenCompare = '';
      content.append(status, grid, empty, browse, compare);
      savedDialog.append(header, element('p', '', 'Your list stays in this browser and does not reserve stock. Prices and availability are refreshed when you open it.'), content);
      document.body.append(savedDialog);
    }
    render();
    savedDialog.showModal();
    refreshLists();
  }
  function restoreFocus(action, id) {
    const candidates = document.querySelectorAll(`[data-guild-${action}]`);
    const activeDialog = dialog?.open ? dialog : savedDialog?.open ? savedDialog : null;
    const target = [...candidates].find((node) => node.dataset[action === 'variant' ? 'guildVariant' : 'guildRemove'] === id && (!activeDialog || activeDialog.contains(node)));
    if (target) target.focus();
    else if (activeDialog) activeDialog.querySelector('button')?.focus({ preventScroll: true });
    else if (action === 'remove') {
      const saved = document.querySelector('[data-guild-saved]');
      const empty = saved?.querySelector('[data-guild-saved-empty]:not([hidden])');
      const next = empty?.querySelector('a[href], button') || saved?.querySelector('[data-guild-remove], a[href]');
      next?.focus({ preventScroll: true });
    }
  }
  function init() {
    readStorage();
    readProducts();
    observeRecommendations();
    render();
    if (document.querySelector('[data-guild-saved]')) refreshLists();
    document.addEventListener('click', (event) => {
      const button = event.target.closest('button');
      if (!button) return;
      if (button.hasAttribute('data-guild-open-compare')) return openComparison();
      if (button.hasAttribute('data-guild-open-saved')) return openSaved();
      if (button.dataset.guildRetry) return refreshLists();
      if (button.dataset.guildRemove) {
        const list = button.dataset.guildList;
        if (!['saved', 'compared'].includes(list)) return;
        state[list] = state[list].filter((ref) => ref.id !== button.dataset.guildRemove);
        persist();
        render();
        restoreFocus('remove', state[list][0]?.id);
        announce('Item removed.', false, button);
        return;
      }
      const action = button.hasAttribute('data-guild-save') ? 'saved' : button.hasAttribute('data-guild-compare') ? 'compared' : '';
      if (!action) return;
      const id = button.dataset[action === 'saved' ? 'guildSave' : 'guildCompare'];
      const existing = state[action].find((ref) => ref.id === id);
      if (existing) state[action] = state[action].filter((ref) => ref.id !== id);
      else {
        const ref = referenceFor(id);
        if (!ref) return announce('Open the product page to check its current details.', false, button);
        if (state[action].length >= (action === 'compared' ? 3 : 40)) return announce(action === 'compared' ? 'Compare up to three products. Remove one selection to add another.' : 'Your saved list is full. Remove an item to save another.', false, button);
        state[action].push(ref);
      }
      persist();
      updateButtons();
      if (action === 'saved' && !existing && persistent && !pixelsOn()) respond(button, 'cg-save-pop');
      // The editorial characters wave at a save, as they do on the home page.
      if (action === 'saved' && !existing) document.dispatchEvent(new CustomEvent('cg:saved', { detail: { id } }));
      if (document.querySelector('[data-guild-saved]') || dialog?.open) render();
      const savedMessage = persistent ? 'Saved in this browser. This does not reserve stock.' : 'Added to this page’s list. This does not reserve stock.';
      announce(existing ? 'Item removed.' : action === 'saved' ? savedMessage : 'Added to comparison. Open Compare selections to view the details.', action === 'saved' && !existing, button);
    });
    document.addEventListener('change', (event) => {
      if (!event.target.matches('[data-guild-variant]')) return;
      const id = event.target.dataset.guildVariant;
      const value = event.target.value;
      if (value && !records.get(id)?.product?.variants.some((variant) => String(variant.id) === value)) return;
      selected.set(id, value);
      [...state.saved, ...state.compared].filter((ref) => ref.id === id).forEach((ref) => { ref.variantId = value; });
      persist();
      render();
      restoreFocus('variant', id);
      announce('Variant selection updated.', false, event.target);
    });
    window.addEventListener('storage', (event) => {
      if (event.key !== storageKey) return;
      readStorage();
      render();
      if (document.querySelector('[data-guild-saved]') || dialog?.open) refreshLists();
    });
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      readStorage();
      render();
      if (document.querySelector('[data-guild-saved]')) refreshLists();
    });
    document.addEventListener('shopify:section:load', (event) => { readProducts(event.target); observeRecommendations(event.target); render(); });
    document.addEventListener('guild:products:updated', (event) => {
      readProducts(event.target);
      observeRecommendations(event.target);
      updateButtons();
    });
    document.addEventListener('focusin', (event) => {
      const target = event.target;
      requestAnimationFrame(() => { if (target === document.activeElement) keepComparisonFocusClear(target); });
    });
    document.addEventListener('shopify:section:unload', (event) => {
      [...activeResponses.keys()].filter((node) => event.target.contains(node)).forEach(stopResponse);
    });
    document.addEventListener('visibilitychange', stopResponses);
    document.addEventListener('guild:motion-change', stopResponses);
    document.addEventListener('guild:experience-change', () => {
      stopResponses();
      const notice = document.querySelector('.cg-commerce-notice');
      notice?.classList.toggle('cg-pixel-save-notice', pixelsOn() && Boolean(notice.querySelector('.cg-pixel-storage')));
      if (notice?.textContent && !notice.classList.contains('cg-commerce-notice-inline')) notice.classList.add('cg-commerce-notice-suppressed');
    });
    const settleNoticePlacement = () => {
      const notice = document.querySelector('.cg-commerce-notice');
      if (notice?.textContent && !notice.classList.contains('cg-commerce-notice-inline')) {
        stopResponses();
        // Scrolling may move content into the previously clear corner. Hide the
        // floating decoration, keeping its accessible status without layout work.
        notice.classList.add('cg-commerce-notice-suppressed');
      }
    };
    window.addEventListener('scroll', settleNoticePlacement, { passive: true });
    window.addEventListener('resize', settleNoticePlacement, { passive: true });
    window.addEventListener('pagehide', stopResponses);
    reducedMotion.addEventListener('change', stopResponses);
    narrowScreen.addEventListener('change', stopResponses);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (!data?.variant) return;
        if (data.html) readProducts(data.html);
        const productInfo = document.querySelector(`product-info[data-section="${CSS.escape(String(data.sectionId))}"]`);
        const script = productInfo?.querySelector('script[data-guild-product]');
        let product;
        try { product = script ? JSON.parse(script.textContent) : null; } catch { return; }
        if (!product) product = [...records.values()].map((record) => record.product).find((candidate) => candidate?.variants.some((variant) => String(variant.id) === String(data.variant.id)));
        if (product && validId(data.variant.id)) {
          selected.set(String(product.id), String(data.variant.id));
          [...state.saved, ...state.compared].filter((ref) => ref.id === String(product.id)).forEach((ref) => { ref.variantId = String(data.variant.id); });
          persist();
          updateButtons();
        }
      });
    }
  }
  window.GuildCommerce = { refresh: refreshLists };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
